import { Box, Flex, Stack, Wrap, WrapItem } from '@chakra-ui/react'
import { faComment, faSmilePlus } from '@fortawesome/pro-regular-svg-icons'
import { useEffect, useState } from 'react'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import { isUserInChannel } from '@/client/services/worlds'

import { REACTION_BUTTON_CLASS_NAME } from '@/components/Community/Post/constants'
import PostStatusSection from '@/components/Community/Post/internal/PostStatusSection'
import PostStatusTags from '@/components/Community/Post/internal/PostStatusTags'
import type { PostDetails } from '@/components/Community/Post/types'
import { usePlatformLayoutContext } from '@/components/Layout/context'
import Tippy from '@/components/UI/Tippy'
import { getProcessedReactions, getReactionListWithNewReaction } from '@/components/Worlds/helpers'
import { MAX_REACTIONS_TO_SHOW } from '@/components/Worlds/Reaction/constants'
import ReactionButton from '@/components/Worlds/Reaction/ReactionButton'
import ReactionWrapItems from '@/components/Worlds/Reaction/ReactionWrapItems'
import type { ReactionState } from '@/components/Worlds/types'
import IconButtonWithNumber from '@/components/Worlds/UI/Button/IconButtonWithNumber'

export default function PostFooter({
  post,
  showCommentButton = false,
  showPostStatusSection = false,
}: {
  post: PostDetails
  showCommentButton?: boolean
  showPostStatusSection?: boolean
}): JSX.Element {
  const user = useUser()

  const canUserReact = isUserInChannel({ channelId: post.channel.id, user })

  const { toggleExpandPostId } = usePlatformLayoutContext()
  const { countReplies, reactions } = post
  const postStatus = post.liveHelpInfo?.postStatus ?? null
  const currentLiveHelpRequest = post.liveHelpInfo?.currentLiveHelpRequest ?? null

  const parsedReactions = getProcessedReactions({ reactions })

  const [currentReactions, setCurrentReactions] = useState<ReactionState[]>(parsedReactions)
  const [showAllReactions, setShowAllReactions] = useState(false)

  useEffect(() => setCurrentReactions(getProcessedReactions({ reactions })), [reactions])

  const [onToggleReaction] = useAPIWrapper(
    async (newReaction: string) => {
      // Optimistic update
      const oldReactions = [...currentReactions]

      const newReactions = getReactionListWithNewReaction({
        reactions: currentReactions,
        newReaction,
        userId: user.id,
        userDisplayName: user.displayName,
      })

      setCurrentReactions(newReactions)

      if (
        oldReactions.length <= MAX_REACTIONS_TO_SHOW &&
        newReactions.length > MAX_REACTIONS_TO_SHOW
      ) {
        // If we pass the threshold, expand all reactions to reduce potential confusion
        setShowAllReactions(true)
      }

      try {
        await RPC.togglePostReaction(
          {
            postId: post.id,
            reaction: newReaction,
          },
          {
            disableProgressBar: true,
          }
        )
      } catch (err) {
        // Revert optimistic update
        setCurrentReactions(oldReactions)
        throw err
      }
    },
    [post.id, user.displayName, currentReactions, user.id],
    'Toggle Post Reaction'
  )

  return (
    <Stack
      direction={{ base: 'column', md: 'row' }}
      spacing="24px"
      justify="space-between"
      align={{ base: 'auto', md: 'flex-end' }}
    >
      <Wrap spacing="8px">
        {showCommentButton ? (
          <WrapItem>
            <IconButtonWithNumber
              icon={faComment}
              onClick={() => toggleExpandPostId(post.id)}
              label={countReplies}
            />
          </WrapItem>
        ) : null}
        <ReactionWrapItems
          reactions={currentReactions}
          canUserReact={canUserReact}
          onToggleReaction={onToggleReaction}
          showAll={showAllReactions}
          setShowAll={setShowAllReactions}
        />
        <WrapItem>
          <Box className={REACTION_BUTTON_CLASS_NAME}>
            <ReactionButton
              onReact={onToggleReaction}
              disabled={!canUserReact}
              buttonComponent={({ active }) => (
                <Tippy disabled={canUserReact} content="Join this subworld to react!">
                  <IconButtonWithNumber
                    icon={faSmilePlus}
                    highlighted={active}
                    isDisabled={!canUserReact}
                  />
                </Tippy>
              )}
            />
          </Box>
        </WrapItem>
      </Wrap>
      {postStatus != null ? (
        <Flex justify="flex-end">
          {showPostStatusSection ? (
            <PostStatusSection
              postId={post.id}
              postUserId={post.user.id}
              status={postStatus}
              liveHelpRequest={currentLiveHelpRequest}
            />
          ) : (
            <PostStatusTags status={postStatus} currentLiveHelpRequest={currentLiveHelpRequest} />
          )}
        </Flex>
      ) : null}
    </Stack>
  )
}
